<script setup lang="ts">
/**
 * 本文中のNGワードマーキングの表示切り替え。
 * 親チェックボックス(全体)＋レベル別チェックボックスの2段構成。
 *
 * レベル別に件数を併記しているのは、この商品の重症度プロファイルが一目で分かるため。
 * 「Lv.5 (18)」と見えれば、最初からLv.5を切って作業する判断ができる。
 */
import { computed, ref, watch } from 'vue'
import type { NgWordMatch } from '@/types/api'
import { levelBucket, type NgLevelBucket } from '@/utils/ngHighlight'
import { useNgMarkStore } from '@/stores/ngMark'

const props = defineProps<{
  ngWords: NgWordMatch[] | null | undefined
}>()

const ngMark = useNgMarkStore()

const LEVELS: { key: NgLevelBucket; label: string; styleLabel: string }[] = [
  { key: 9, label: 'Lv.9', styleLabel: '赤・波線' },
  { key: 7, label: 'Lv.7', styleLabel: '橙・実線' },
  { key: 5, label: 'Lv.5', styleLabel: '青・実線' },
]

const counts = computed(() => {
  const c: Record<NgLevelBucket, number> = { 9: 0, 7: 0, 5: 0 }
  for (const w of props.ngWords ?? []) c[levelBucket(w.level)]++
  return c
})

// 親チェックボックスの中間状態(indeterminate)はDOMプロパティなので直接設定する
const allRef = ref<HTMLInputElement | null>(null)
watch(
  () => [ngMark.enabled, ngMark.isPartial],
  () => {
    if (allRef.value) allRef.value.indeterminate = ngMark.enabled && ngMark.isPartial
  },
  { immediate: true, flush: 'post' },
)
</script>

<template>
  <div class="ng-filter">
    <label class="ng-filter-all">
      <input
        ref="allRef"
        type="checkbox"
        :checked="ngMark.enabled"
        @change="ngMark.setEnabled(($event.target as HTMLInputElement).checked)"
      />
      本文にNGワードを表示
    </label>

    <label v-for="lv in LEVELS" :key="lv.key" class="ng-filter-lv" :class="`lv${lv.key}`">
      <input
        type="checkbox"
        :checked="ngMark.enabled && ngMark.levels[lv.key]"
        @change="ngMark.setLevel(lv.key, ($event.target as HTMLInputElement).checked)"
      />
      <span class="ng-filter-sample" :class="`sample-lv${lv.key}`"></span>
      {{ lv.label }}
      <span class="ng-filter-count">({{ counts[lv.key] }})</span>
    </label>
  </div>
</template>
